import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import throttle from 'lodash/throttle';
import classnames from 'classnames';
import { FocusScope } from '@react-aria/focus';
import { useTimeout } from 'site-modules/shared/hooks/use-timeout';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { UserProfileModel } from 'client/data/models/profile/profile';
import { PageModel } from 'client/data/models/page';
import { VisitorModel } from 'client/data/models/visitor';
import { getQuery } from 'client/utils/location';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { createDataToSubscriptions } from 'site-modules/shared/utils/profile/email-subscriptions-helper';
import { EDMUNDS_LOGO_TEXT } from 'client/images/inline-svgs/edmunds-logo-text';
import { LoadingSpinner } from 'site-modules/shared/components/loading-spinner/loading-spinner';
import { SignEmailForm } from 'site-modules/shared/components/profile/smart-sign/sign-email-form/sign-email-form';

import './email-gate.scss';

const TRIGGER_ELEMENT_SELECTOR = 'h1';
const EMAIL_GATE_CAMPAIGN = 'brand_content_email_gate';
const CREATIVE_ID = 'email-gate';
const NEWS_SUBSCRIPTION = 'content_series';
const HEADING_ID = 'gate-heading';
const DESCRIPTION_ID = 'gate-description';
const HIGHLIGHTS = [
  { icon: 'icon-steering-wheel', label: 'Rankings, reviews, video test drives' },
  { icon: 'icon-clipboard2', label: '500k+ miles of test drives every year' },
  { icon: 'icon-meter', label: '400+ new vehicle reviews annually' },
];

function EmailGateUI({
  location,
  isEmailGatePassed,
  idmData,
  removeDataFromIdm,
  createOrUpdateIdmData,
  setModelValue,
  isAuthenticated,
  disableEmailGate,
  isMobile,
}) {
  const [isTriggered, setTriggered] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitted, setSubmitted] = useState(false);
  const [isFinished, setFinished] = useState(false);
  const [setFinishTimeout, clearFinishTimeout] = useTimeout();
  const containerRef = useRef();
  const { utm_campaign: utmCampaign } = getQuery(location);
  const profileEmail = idmData?.identifiers?.email?.[0];
  const isSubscribed = !!profileEmail && !idmData?.marketing?.campaigns?.[NEWS_SUBSCRIPTION]?.unsubscribedTs;
  const canBeTriggered =
    !disableEmailGate &&
    isEmailGatePassed !== null &&
    !isEmailGatePassed &&
    isAuthenticated !== null &&
    !isSubscribed &&
    utmCampaign === EMAIL_GATE_CAMPAIGN;
  const isEnabled = isTriggered && (isSubmitted || isLoading || canBeTriggered);

  useEffect(() => {
    const onScroll = throttle(() => {
      setTriggered(document.querySelector(TRIGGER_ELEMENT_SELECTOR)?.getBoundingClientRect?.()?.bottom < 0);
    }, 30);

    if (!isTriggered && canBeTriggered) {
      document.addEventListener('scroll', onScroll);
    }

    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [isTriggered, canBeTriggered]);

  useEffect(() => {
    if (isEnabled) {
      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
        event_data: {
          action_name: TrackingConstant.ACTION_WIDGET_VIEW,
          action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
          creative_id: CREATIVE_ID,
        },
      });

      document?.getElementById('sign-up-email')?.focus();
    }
  }, [isEnabled]);

  useEffect(() => {
    if (isEnabled) {
      document.body.classList.toggle('overflow-hidden', !isSubmitted);
    }
  }, [isEnabled, isSubmitted]);

  const onSubmit = useCallback(
    async ({ emailValue }) => {
      setLoading(true);

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          action_name: TrackingConstant.ACTION_SIGN_UP,
          subaction_name: TrackingConstant.SUCCESS_EMAIL_ONLY_SIGN_UP,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          creative_id: CREATIVE_ID,
        },
      });

      try {
        if (profileEmail && emailValue === profileEmail) {
          await removeDataFromIdm(createDataToSubscriptions([NEWS_SUBSCRIPTION], 'subscribe').values);
        } else {
          await createOrUpdateIdmData({
            idmDataToSave: { identifiers: { email: [emailValue] } },
            signInAnonymouslyTracking: { creativeId: CREATIVE_ID },
          });
        }

        await setModelValue('emailGatePassed', VisitorModel, 'true');
        setLoading(false);
        setSubmitted(true);
        setFinishTimeout(() => {
          setFinished(true);
        }, 2300);
      } catch {
        setLoading(false);
      }
    },
    [createOrUpdateIdmData, profileEmail, removeDataFromIdm, setFinishTimeout, setModelValue]
  );

  function close() {
    clearFinishTimeout();
    setFinished(true);
  }

  if (!isEnabled || isFinished) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={classnames('email-gate pos-f top-0 bottom-0 left-0 right-0 d-flex flex-column', {
        submitted: isSubmitted,
      })}
      data-tracking-parent={CREATIVE_ID}
    >
      <FocusScope contain>
        <div className="email-gate-overlay bg-white h-100 w-100" />
        <div className="email-gate-box w-100 bg-white d-flex justify-content-center">
          <div className="email-gate-content pos-r text-md-center mw-100 px-1_5 pt-1_5 pb-3_5 pt-md-2">
            {EDMUNDS_LOGO_TEXT({
              'aria-label': 'Edmunds',
              iconFillColor: '#007ee5',
              fillColor: '#4f514f',
              width: '100px',
              className: 'mb-0_5 mb-md-1',
            })}
            <div className="pos-r">
              <div className={classnames({ invisible: isSubmitted })}>
                <h2 id={HEADING_ID} className="heading-3 mb-0_75 mb-md-0_25">
                  Unlock Car Reviews and Auto News
                </h2>
                <div id={DESCRIPTION_ID} className="mb-1_5 mb-md-2">
                  Sign up for free. No spam, only expert insights and smart shopping tools.
                </div>
                <LoadingSpinner isLoading={isLoading}>
                  <SignEmailForm
                    isAuthenticated={isAuthenticated}
                    successCallback={onSubmit}
                    profileDataEmail={profileEmail}
                    isVerifyEmail={false}
                    className="text-start mb-1_5 mb-md-2_75"
                    labelText="Email Address"
                    ctaText="Subscribe"
                    disclaimerPrefix="We will not share your email with anyone else. "
                    labelClassName="small mb-0_5"
                    buttonClassName="text-transform-none fw-medium size-16 px-1_5 py-0_5"
                    disclaimerClassName="small mt-0_5"
                    describedById={`${HEADING_ID} ${DESCRIPTION_ID}`}
                    isSignInAction
                    isInlineButton
                  />
                </LoadingSpinner>
                <h3 className="heading-4 text-center mb-1_5">Take it from the best editorial team in the industry</h3>
                <ul
                  className={classnames('list-unstyled d-flex justify-content-around text-center fw-medium', {
                    small: isMobile,
                    medium: !isMobile,
                  })}
                >
                  {HIGHLIGHTS.map(({ icon, label }) => (
                    <li key={icon} className="d-flex flex-column align-items-center px-0_25 px-md-2">
                      <i
                        className={classnames(
                          'highlight-icon d-flex align-items-center justify-content-center size-24 text-primary fw-normal mb-0_5',
                          icon
                        )}
                        style={{ height: '48px', width: '48px' }}
                        aria-hidden
                      />
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
              {isSubmitted && (
                <div className="thank-you-page pos-a top-0">
                  <h2 className="heading-3 mb-0">You&apos;re in! Exclusive content is coming soon to your inbox.</h2>
                </div>
              )}
            </div>
            {isSubmitted && (
              <button
                onClick={close}
                className="close-btn text-cool-gray-10 bg-transparent border-0 rounded-8 pos-a top-0 right-0 py-0_5 px-1"
              >
                <span className="visually-hidden">Close</span>
                <i className="icon-cross2 ms-0_25" aria-hidden />
              </button>
            )}
          </div>
        </div>
      </FocusScope>
    </div>
  );
}

EmailGateUI.propTypes = {
  removeDataFromIdm: PropTypes.func.isRequired,
  createOrUpdateIdmData: PropTypes.func.isRequired,
  setModelValue: PropTypes.func.isRequired,
  idmData: PropTypes.shape({}),
  location: PropTypes.shape({
    query: PropTypes.objectOf(PropTypes.string),
  }),
  isEmailGatePassed: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
  disableEmailGate: PropTypes.bool,
  isMobile: PropTypes.bool,
};

EmailGateUI.defaultProps = {
  idmData: null,
  location: null,
  isEmailGatePassed: null,
  isAuthenticated: false,
  disableEmailGate: false,
  isMobile: false,
};

const mapStateToProps = state => ({
  isAuthenticated: get(state, 'profile.isAuthenticated'),
  isMobile: get(state, 'mobile'),
  disableEmailGate: !!get(state, 'featureFlags["disable-email-gate"]'),
});

export const EmailGate = connect(mapStateToProps)(
  connectToModel(insiderMethods(EmailGateUI), {
    location: bindToPath('location', PageModel),
    idmData: bindToPath(({ isAuthenticated }) => (isAuthenticated ? 'data.idm' : ''), UserProfileModel),
    isEmailGatePassed: bindToPath('emailGatePassed', VisitorModel),
  })
);
